import React, { useState } from 'react';
import {
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import ViewColumnRoundedIcon from '@mui/icons-material/ViewColumnRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import IconButton from '../../Button/IconButton';
import useTranslate from '../../../Hooks/useTranslate';
import useHasPermission from '../../../Hooks/useHasPermission';
import useMobileMode from '../../../Hooks/useMobileMode';
import LayoutEditor from './LayoutEditor';
import useLayoutEdit from './useLayoutEdit';
import { SURFACE_LABELS, isInPlaceSurface } from './surfaceLabels';
import { readStored } from '../dialect';

const SURFACE_ICONS = {
  list: ViewColumnRoundedIcon,
  detail: DashboardRoundedIcon,
  filters: FilterAltRoundedIcon,
  sorting: SortRoundedIcon,
  search: SearchRoundedIcon,
};

/**
 * Opens the layout editor for one of a section's surfaces, chosen from the cog's menu. Renders
 * nothing without the permission that the catalogue and the save both enforce, and nothing at all
 * on mobile - the layouts it edits are rendered there, but editing them is a desktop task.
 *
 * Each surface is its own layout with its own save, so the menu picks which one is being edited.
 * A surface arranged on the page it draws takes the page over instead of opening a dialog.
 *
 * @param {Object} props - Component props.
 * @param {String} props.sectionName - The section being configured.
 * @param {Array} props.surfaces - The surfaces to offer, as `{ surface, defaultLayout }`.
 * @param {Array} props.parameters - Runtime values the page publishes to the fields it renders.
 * @param {String} props.size - The size of the cog, where a row has no room for a full one.
 *
 * @returns {React.JSX.Element}
 */
const LayoutEditButton = function LayoutEditButton({
  sectionName,
  surfaces = [],
  parameters = [],
  size = 'medium',
}) {
  const translate = useTranslate();
  const canConfigure = useHasPermission('customers.configurecolumns');
  const isMobile = useMobileMode();
  const { session, start } = useLayoutEdit();
  const [anchor, setAnchor] = useState(null);
  const [editing, setEditing] = useState(null);

  // Nothing to offer while a layout is already being arranged: the toolbar the mode puts in the
  // navbar is what acts on it, and the cog would only open the mode it is already in.
  if (
    isMobile ||
    session ||
    !canConfigure ||
    !sectionName ||
    surfaces.length === 0
  )
    return null;

  return (
    <>
      <IconButton
        id="edit-layout-icnBtn"
        title="EDIT_LAYOUT"
        size={size}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <SettingsRoundedIcon fontSize={size === 'small' ? 'small' : 'medium'} />
      </IconButton>
      <Menu
        id="edit-layout-menu"
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {surfaces.map(({ surface, defaultLayout, separated }) => {
          // What the editor arranges is the layout's content; the document around it is the read
          // path's business.
          const checkedIn = readStored(defaultLayout)?.layout ?? [];
          const Icon = SURFACE_ICONS[surface] ?? SettingsRoundedIcon;

          return [
            // A surface that is not part of the filters dialog is set apart from the ones that are.
            separated && <Divider key={`${surface}-divider`} />,
            <MenuItem
              key={surface}
              onClick={() => {
                if (isInPlaceSurface(surface))
                  start({
                    sectionName,
                    surface,
                    defaultLayout: checkedIn,
                    parameters,
                  });
                else setEditing(surface);

                setAnchor(null);
              }}
            >
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={translate(SURFACE_LABELS[surface] ?? surface)}
              />
            </MenuItem>,
          ];
        })}
      </Menu>
      <LayoutEditor
        open={editing !== null}
        close={() => setEditing(null)}
        sectionName={sectionName}
        surface={editing ?? 'list'}
        defaultLayout={
          readStored(
            surfaces.find(({ surface }) => surface === editing)?.defaultLayout
          )?.layout ?? []
        }
        parameters={parameters}
      />
    </>
  );
};

export default LayoutEditButton;
